import { forwardRef } from 'react';
import type { PropsWithoutRef, Ref } from 'react';

import type { BaseProps, ForwardRefForwardPropsComponent, NoChildrenProp } from '../../types';
import { useConfiguration } from '../../hooks';
import VisuallyHiddenText from '../VisuallyHiddenText';
import NoValue from '../NoValue/NoValue';

import { formatCurrency, isValidCurrency } from './utils';

export interface CurrencyDisplayProps extends BaseProps, NoChildrenProp {
  /** Number value. */
  value?: number | bigint;
  /** Three letter currency shorthand ISO code. If not provided or invalid, component renders only the number. */
  currencyISOCode?: string;
  /** Options to define formatting. */
  formattingOptions?: {
    currency?: 'symbol' | 'code' | 'name';
    /**
     * Determines negative value notation: using minus sign or parentheses.
     * @default 'minus-sign'
     */
    negative?: 'minus-sign' | 'parentheses';
    /**
     * Value display notation.
     * @default 'standard'
     */
    notation?: 'standard' | 'compact';
    /**
     * Fixed number of fraction digits. Maximum is 20, default is currency dependant.
     * @default variable
     */
    fractionDigits?: number;
    /** Determines whether group separators should be shown.
     * @default true
     */
    groupSeparators?: boolean;
  };
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLAnchorElement>;
}

const CurrencyDisplay: ForwardRefForwardPropsComponent<CurrencyDisplayProps> = forwardRef(
  function CurrencyDisplay(
    {
      value,
      currencyISOCode,
      formattingOptions: {
        currency = 'symbol',
        negative = 'minus-sign',
        notation = 'standard',
        groupSeparators = true,
        fractionDigits
      } = {},
      ...restProps
    }: PropsWithoutRef<CurrencyDisplayProps>,
    ref: CurrencyDisplayProps['ref']
  ) {
    const { locale } = useConfiguration();

    const options =
      currencyISOCode && isValidCurrency(currencyISOCode)
        ? ({
            style: 'currency',
            currency: currencyISOCode,
            currencyDisplay: currency,
            useGrouping: groupSeparators,
            notation,
            currencySign: negative === 'parentheses' ? 'accounting' : 'standard',
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits
          } as Intl.NumberFormatOptions)
        : undefined;

    const srOptions = {
      ...options,
      useGrouping: false,
      currencySign: 'standard',
      currencyDisplay: 'name'
    } satisfies Intl.NumberFormatOptions;

    return (
      <span ref={ref} {...restProps}>
        {value || value === 0 ? (
          <>
            <span aria-hidden>{formatCurrency(value, { locale, options })}</span>
            <VisuallyHiddenText>
              {formatCurrency(value, { locale, options: srOptions })}
            </VisuallyHiddenText>
          </>
        ) : (
          <NoValue />
        )}
      </span>
    );
  }
);

export default CurrencyDisplay;
